import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type * as React from 'react';
import { cn } from '@/lib/cn';

// Zenith (WEB-100): botão é PILL (`rounded-full`, fora da escala de radius), separação vem
// de sombra, não de borda 1px. `active:scale-[0.98]` em todas as variantes.
//
// `min-h-10 min-w-10` até `sm` continua: área de toque de 40px no celular (`spec/17` §7
// item 12, cobrado por `e2e/responsive.spec.ts`). A utility `touch-44` do DeztyBot não
// serve aqui porque ela estende a área por `::after` e o teste mede a caixa real.
const buttonVariants = cva(
  "inline-flex min-h-10 min-w-10 shrink-0 items-center justify-center gap-2 rounded-full border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-[background-color,border-color,color,box-shadow,opacity] duration-150 ease-out-strong outline-none select-none active:scale-[0.98] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 sm:min-h-0 sm:min-w-0 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // Pill INK escura: a ação primária do Zenith.
        default:
          'bg-primary text-primary-foreground shadow-(--shadow-pill) hover:bg-[color-mix(in_oklch,var(--primary),white_14%)]',
        // Tinta de estado, sem pill vermelha chapada (DESIGN do Zenith: cor só em estado).
        destructive:
          'bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20',
        // Fill tintado com inset em vez de borda dura, mesma linguagem dos inputs.
        outline:
          'bg-secondary/60 text-foreground shadow-(--shadow-inset) hover:bg-secondary aria-expanded:bg-secondary',
        secondary:
          'bg-secondary text-secondary-foreground shadow-(--shadow-pill) hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)]',
        ghost: 'hover:bg-secondary hover:text-foreground aria-expanded:bg-secondary',
        link: 'text-teal underline-offset-4 hover:text-teal-hover hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        xs: "h-6 gap-1 px-2.5 text-xs has-[>svg]:px-2 [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-xs': "size-6 [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
